import { createFileRoute } from '@tanstack/react-router';
import { PageShell, Section } from '~/components/layout';
import { CtaBanner, PressStrip } from '~/components/sections';
import { listNewsArticles, listPressItems, newsArticleHref } from '~/content/news';
import { pageHeadMeta } from '~/lib/seo';
import helpStylesHref from '~/styles/help.css?url';

export const Route = createFileRoute('/news')({
  loader: () => ({
    articles: listNewsArticles().map(({ slug, title, subtitle, date, publishedAt, excerpt }) => ({
      slug,
      title,
      subtitle,
      date,
      publishedAt,
      excerpt,
    })),
    press: listPressItems(),
  }),
  head: () =>
    pageHeadMeta({
      title: 'News and announcements | nsave',
      description: 'The latest company news, announcements and press releases from nsave.',
      path: '/news',
      stylesheets: [helpStylesHref],
    }),
  component: NewsPage,
});

/**
 * Newsroom index. Same headline and list styling as /help, without the
 * categories sidebar or search: the list sits alone in a centred column.
 */
function NewsPage() {
  const { articles, press } = Route.useLoaderData();

  return (
    <PageShell>
      <Section className="pt-16 !pb-0 md:pt-24">
        <div className="mx-auto flex max-w-3xl flex-col items-center text-center">
          <h1 className="text-[40px] leading-[1.1] font-semibold tracking-[-0.03em] text-balance md:text-title-m">
            News
          </h1>
          <p className="mt-5 text-body text-grey-700">
            Announcements and press releases from nsave.
          </p>
        </div>
      </Section>

      <Section className="!pt-[96px] !pb-0 md:!pt-[144px]">
        <div className="mx-auto max-w-[760px]">
          <h2 className="text-[15px] font-medium tracking-[0.27px] text-grey-500 uppercase">
            Announcements
          </h2>
          {articles.length > 0 ? (
            <ul className="group/list mt-4 flex flex-col">
              {articles.map((article) => (
                <li key={article.slug} className="border-b border-black-6 first:border-t">
                  <a
                    href={newsArticleHref(article)}
                    className="group flex flex-col gap-3 py-6 transition-opacity ease-nav duration-150 group-has-[a:hover]/list:opacity-40 hover:!opacity-100">
                    <time
                      dateTime={article.publishedAt}
                      className="text-body-s font-medium text-grey-500">
                      {article.date}
                    </time>
                    <h3 className="text-h5 font-semibold tracking-[-0.02em] text-ink">
                      <span className="link-underline-lr bg-origin-content">{article.title}</span>
                    </h3>
                    <p className="learn-article-excerpt text-body text-grey-900">
                      {article.excerpt}
                    </p>
                  </a>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-8 text-body text-grey-700">No news yet.</p>
          )}
        </div>
      </Section>

      <Section className="!pt-[96px] !pb-[112px] md:!pt-[128px]">
        <h2 className="border-b border-black-6 pb-4 text-[15px] font-medium tracking-[0.27px] text-grey-500 uppercase">
          Press
        </h2>
        <PressStrip items={press} className="mt-6" />
      </Section>

      <CtaBanner />
    </PageShell>
  );
}
